import { HTTPError } from 'ky';

import type { SumsubReviewAnswer, SumsubReviewRejectType, SumsubReviewStatus } from '@/features/verification/constants/sumsub';
import { fastApi } from '@/services/api';

const PATH = 'v1/customer/verification-information';

export const VERIFICATION_STATUSES = {
  NOT_VERIFIED: 'NOT_VERIFIED',
  VERIFICATION_STARTED: 'VERIFICATION_STARTED',
  VERIFICATION_SUCCEEDED: 'VERIFICATION_SUCCEEDED',
} as const;

export type VerificationStatus = (typeof VERIFICATION_STATUSES)[keyof typeof VERIFICATION_STATUSES];

export type VerificationInformation = {
  verification: {
    level?: string;
    status: VerificationStatus;
    verification_date: string | null;
  };
  aml: {
    date: string | undefined;
  };
};

export const NCI_LEVEL_IDENTIFIER = 'NCI';

export const WORKFLOW_LEVEL_IDENTIFIER = 'WF';

export const FUTURES_VERIFICATION_LEVEL_IDENTIFIERS = [NCI_LEVEL_IDENTIFIER, WORKFLOW_LEVEL_IDENTIFIER] as const;

export type VerificationInformationGetResponse = VerificationInformation;

export const getVerificationInformation = async () => {
  return fastApi.get(PATH).json<VerificationInformationGetResponse>();
};

const SUPPORTING_INFORMATION_PATH = 'v1/customer/form/verification-supporting-information';

export enum VerificationInformationSupportingInformationField {
  CCPT_JUSTIFICATION = 'CCPT_JUSTIFICATION',
  NIDN_JUSTIFICATION = 'NIDN_JUSTIFICATION',
}

export const VERIFICATION_INFORMATION_SUPPORTING_INFORMATION_FIELD_ANSWER_KEYS = {
  [VerificationInformationSupportingInformationField.CCPT_JUSTIFICATION]: 'ccptJustification',
  [VerificationInformationSupportingInformationField.NIDN_JUSTIFICATION]: 'nidnJustification',
} as const satisfies Record<VerificationInformationSupportingInformationField, string>;

type VerificationInformationSupportingInformationAnswerKey =
  (typeof VERIFICATION_INFORMATION_SUPPORTING_INFORMATION_FIELD_ANSWER_KEYS)[VerificationInformationSupportingInformationField];

export enum VerificationInformationSupportingInformationAnswerValue {
  NOT_IN_POSSESSION = 'NOT_IN_POSSESSION',
  EXPIRED = 'EXPIRED',
  RENEWING = 'RENEWING',
  LOST = 'LOST',
}

export const VERIFICATION_INFORMATION_SUPPORTING_INFORMATION_ANSWERS = [
  VerificationInformationSupportingInformationAnswerValue.NOT_IN_POSSESSION,
  VerificationInformationSupportingInformationAnswerValue.EXPIRED,
  VerificationInformationSupportingInformationAnswerValue.RENEWING,
  VerificationInformationSupportingInformationAnswerValue.LOST,
] as const;

export type VerificationInformationSupportingInformationAnswers = Partial<
  Record<VerificationInformationSupportingInformationAnswerKey, VerificationInformationSupportingInformationAnswerValue>
>;

export type VerificationInformationSupportingInformationGetResponse = {
  answers?: VerificationInformationSupportingInformationAnswers;
  date?: string;
  requiredFields: VerificationInformationSupportingInformationField[];
};

export const getVerificationInformationSupportingInformation = async () => {
  return fastApi.get(SUPPORTING_INFORMATION_PATH).json<VerificationInformationSupportingInformationGetResponse>();
};

type VerificationInformationSupportingInformationPostRequestBody = {
  answers: VerificationInformationSupportingInformationAnswers;
};

export const postVerificationInformationSupportingInformation = async (body: VerificationInformationSupportingInformationPostRequestBody) => {
  await fastApi.post(SUPPORTING_INFORMATION_PATH, { json: body });
};

const SUMSUB_TOKEN_PATH = `${PATH}/sumsub-token`;
const SUMSUB_TOKEN_V2_PATH = `${SUMSUB_TOKEN_PATH}-v2`;

export type VerificationInformationSumsubTokenGetResponse = {
  token: string;
};

export const getVerificationInformationSumsubToken = async () => {
  return (await fastApi.get(SUMSUB_TOKEN_PATH).json<VerificationInformationSumsubTokenGetResponse>()).token;
};

export const getVerificationInformationSumsubTokenV2 = async () => {
  return (await fastApi.get(SUMSUB_TOKEN_V2_PATH).json<VerificationInformationSumsubTokenGetResponse>()).token;
};

const SUMSUB_RESULT_PATH = `${PATH}/sumsub-result`;

export type VerificationInformationSumsubResultGetResponse = {
  status: SumsubReviewStatus;
  result?: SumsubReviewAnswer;
  rejectType?: SumsubReviewRejectType;
};

export const getVerificationInformationSumsubResult = async () => {
  return fastApi
    .get(SUMSUB_RESULT_PATH)
    .json<VerificationInformationSumsubResultGetResponse>()
    .catch((error) => {
      if (error instanceof HTTPError && error.response.status === 404) return null;

      throw error;
    });
};
